import { useState, useEffect, useRef, useCallback, useLayoutEffect } from 'react';
import * as pdfjsLib from 'pdfjs-dist';
import { useLang } from '../context/LanguageContext';
import { loadPaperPage, savePaperPage } from '../utils/paperResume';
import { SITE_URL } from '../config/env.js';
import { getCachedPdfBuffer, prefetchPdfBytes, getPdfBytes, clearPaperCache } from '../utils/paperCache';
import { shareLink } from '../utils/nativeShare';
import { useTextSize } from '../context/TextSizeContext';
import { hapticLight } from '../utils/haptics';
import './PaperViewer.css';

pdfjsLib.GlobalWorkerOptions.workerSrc =
  typeof window !== 'undefined'
    ? `${window.location.origin}/pdf.worker.min.mjs`
    : '/pdf.worker.min.mjs';

const MOBILE_BREAKPOINT = 768;
/** Reading steps — mobile goes higher so Telugu body text is legible. */
const ZOOM_STEPS = [1, 1.15, 1.25, 1.28, 1.4, 1.55, 1.75, 2, 2.35, 2.75, 3.25, 3.75, 4.5, 5];
const MAX_ZOOM = 4;
const MAX_ZOOM_MOBILE = 5;
/** Sharp PDF bitmap up to this; beyond it CSS enlarges (avoids WKWebView blank canvas). */
const BITMAP_ZOOM_CAP = 2.6;
const SWIPE_HINT_KEY = 'repatikosam-pv-swipe-hint';
const SWIPE_THRESHOLD = 42;
/** iOS/WebKit blank-canvas risk above ~4096px on a side */
const MAX_CANVAS_SIDE = 4096;

function detectMobileView() {
  if (typeof window === 'undefined') return false;
  return window.innerWidth <= MOBILE_BREAKPOINT;
}

function getTouchDistance(touches) {
  const dx = touches[0].clientX - touches[1].clientX;
  const dy = touches[0].clientY - touches[1].clientY;
  return Math.hypot(dx, dy);
}

function cancelRenderTask(task) {
  if (!task) return;
  try {
    task.cancel();
  } catch {
    /* ignore */
  }
}

async function renderPageToCanvas(
  page,
  canvas,
  box,
  zoomFactor,
  renderTasks,
  pageIndex,
  fitMode = 'contain',
  { constrainToBox = true } = {},
) {
  cancelRenderTask(renderTasks[pageIndex]);
  delete renderTasks[pageIndex];

  const base = page.getViewport({ scale: 1 });
  let fitScale = box.width / base.width;
  if (fitMode === 'contain' && box.height > 0) {
    fitScale = Math.min(fitScale, box.height / base.height);
  }

  const scale = fitScale * Math.min(zoomFactor, BITMAP_ZOOM_CAP);
  const viewport = page.getViewport({ scale });
  const displayScale = fitScale * zoomFactor;
  const displayW = Math.round(base.width * displayScale);
  const displayH = Math.round(base.height * displayScale);
  // Cap DPR × zoom so WKWebView does not return a blank canvas.
  let dpr = Math.min(
    3,
    (window.devicePixelRatio || 1) * Math.min(1.85, Math.max(1, Math.sqrt(Math.min(zoomFactor, BITMAP_ZOOM_CAP)))),
  );
  let bitW = Math.floor(viewport.width * dpr);
  let bitH = Math.floor(viewport.height * dpr);
  if (bitW > MAX_CANVAS_SIDE || bitH > MAX_CANVAS_SIDE) {
    const shrink = Math.min(MAX_CANVAS_SIDE / bitW, MAX_CANVAS_SIDE / bitH);
    dpr *= shrink;
    bitW = Math.floor(viewport.width * dpr);
    bitH = Math.floor(viewport.height * dpr);
  }

  canvas.width = Math.max(1, bitW);
  canvas.height = Math.max(1, bitH);
  const cssW = `${displayW}px`;
  const cssH = `${displayH}px`;
  canvas.style.setProperty('width', cssW, 'important');
  canvas.style.setProperty('height', cssH, 'important');
  if (constrainToBox) {
    canvas.style.setProperty('max-width', '100%', 'important');
    canvas.style.setProperty('max-height', '100%', 'important');
  } else {
    canvas.style.removeProperty('max-width');
    canvas.style.removeProperty('max-height');
  }

  const ctx = canvas.getContext('2d', { alpha: false });
  if (!ctx) return false;
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  // Fill so a failed paint never leaves a white flash
  ctx.fillStyle = '#f2f2f2';
  ctx.fillRect(0, 0, viewport.width, viewport.height);

  const task = page.render({ canvasContext: ctx, viewport });
  renderTasks[pageIndex] = task;

  try {
    await task.promise;
  } catch (err) {
    const cancelled =
      err?.name === 'RenderingCancelledException' ||
      err?.name === 'AbortException' ||
      /cancel/i.test(err?.message || '');
    if (cancelled) return false;
    throw err;
  } finally {
    if (renderTasks[pageIndex] === task) delete renderTasks[pageIndex];
  }
  return true;
}

function Toolbar({
  t,
  dateLabel,
  phase,
  pageCount,
  currentPage,
  zoomScale,
  zoomPercent,
  minZoom = 1,
  maxZoom = MAX_ZOOM,
  onZoomIn,
  onZoomOut,
  onZoomReset,
  onShare,
  onClose,
  hideClose = false,
}) {
  return (
    <header className="pv-toolbar">
      <div className="pv-toolbar-brand">
        <span className="pv-toolbar-logo">📰</span>
        <div className="pv-toolbar-meta">
          <span className="pv-toolbar-edition">{t.sharePaperBadge}</span>
          {dateLabel && <span className="pv-toolbar-date">{dateLabel}</span>}
        </div>
      </div>
      {phase === 'ready' && pageCount > 0 && (
        <span className="pv-toolbar-pages">
          {t.paperPageOf} {currentPage + 1} / {pageCount}
        </span>
      )}
      <div className="pv-toolbar-actions">
        <div className="pv-zoom-inline" role="group" aria-label={t.paperZoomLabel}>
          <button type="button" className="pv-zoom-btn" onClick={onZoomOut} disabled={zoomScale <= minZoom} aria-label={t.paperZoomOut}>−</button>
          <span className="pv-zoom-level">{zoomPercent}%</span>
          <button type="button" className="pv-zoom-btn" onClick={onZoomIn} disabled={zoomScale >= maxZoom} aria-label={t.paperZoomIn}>+</button>
          <button type="button" className="pv-zoom-reset" onClick={onZoomReset} disabled={zoomScale <= minZoom}>{t.paperZoomReset}</button>
        </div>
        <button type="button" className="pv-share" onClick={onShare} title={t.shareWhatsAppTitle} aria-label={t.shareWhatsAppTitle}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
          </svg>
        </button>
        {!hideClose && (
          <button type="button" className="pv-close" onClick={onClose} aria-label={t.closeBtn}>✕</button>
        )}
      </div>
    </header>
  );
}

export default function PaperViewer({
  onClose,
  pdfPath,
  waitingForPath = false,
  dateLabel,
  resumeKey = null,
  maxPages = null,
  onPreviewSubscribe,
  embedded = false,
}) {
  const { t } = useLang();
  const { textSize } = useTextSize();
  const [isMobile, setIsMobile] = useState(detectMobileView);
  // Fixed reading comfort on phone — zoom-out always lands here (132%).
  const comfortZoom = isMobile
    ? 1.32
    : textSize === 'lg'
      ? 1.28
      : textSize === 'sm'
        ? 1
        : 1.12;
  const panThreshold = comfortZoom + 0.05;
  const pageLimit = maxPages != null && maxPages > 0 ? maxPages : null;
  const [phase, setPhase] = useState('loading');
  const [pageCount, setPageCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const maxZoom = isMobile ? MAX_ZOOM_MOBILE : MAX_ZOOM;
  const [zoomScale, setZoomScale] = useState(1);
  const [rendering, setRendering] = useState(false);
  const [contentShield, setContentShield] = useState(false);
  const [swipeOffset, setSwipeOffset] = useState(0);
  const [showSwipeHint, setShowSwipeHint] = useState(false);
  const [pageTransition, setPageTransition] = useState('');
  const [reloadNonce, setReloadNonce] = useState(0);

  const scrollRef = useRef(null);
  const footerRef = useRef(null);
  const backdropRef = useRef(null);
  const viewportRef = useRef(null);
  const canvasRef = useRef(null);
  const pdfRef = useRef(null);
  const renderGenRef = useRef(0);
  const renderTasksRef = useRef({});
  const lastRenderKeyRef = useRef('');
  const loadGenRef = useRef(0);
  const touchRef = useRef({ x: 0, y: 0, active: false });
  const panRef = useRef({ active: false, x: 0, y: 0, scrollLeft: 0, scrollTop: 0 });
  const pinchRef = useRef({ active: false, dist0: 0, scale0: 1 });
  const zoomDebounceRef = useRef(null);
  const gestureZoomRef = useRef(null);
  const pendingZoomScrollRef = useRef(null);
  const lastTapRef = useRef(0);
  const [gestureZoom, setGestureZoom] = useState(null);

  const getPageBox = useCallback(() => {
    const el = viewportRef.current;
    const vv = window.visualViewport;
    const rawW = el?.clientWidth || 0;
    const vw = Math.floor(rawW > 80 ? rawW : vv?.width || window.innerWidth);
    let vh = Math.floor(el?.clientHeight || 0);
    if (!vh || vh < 100) {
      vh = embedded
        ? Math.max(280, Math.floor((vv?.height ?? window.innerHeight) - 160))
        : Math.floor((vv?.height ?? window.innerHeight) * (isMobile ? 0.55 : 0.88));
    }
    const pad = embedded ? 0 : isMobile ? 8 : 2;
    return {
      width: Math.max(280, vw - pad * 2),
      height: Math.max(280, vh - pad * 2),
    };
  }, [isMobile, embedded]);

  const invalidateRender = useCallback(() => {
    lastRenderKeyRef.current = '';
    cancelRenderTask(renderTasksRef.current[currentPage]);
    delete renderTasksRef.current[currentPage];
  }, [currentPage]);

  const cancelAllRenderTasks = useCallback(() => {
    Object.keys(renderTasksRef.current).forEach((key) => {
      cancelRenderTask(renderTasksRef.current[key]);
    });
    renderTasksRef.current = {};
  }, []);

  const renderCurrentPage = useCallback(async () => {
    const pdf = pdfRef.current;
    const canvas = canvasRef.current;
    if (!pdf || !canvas || phase !== 'ready') return;

    const box = getPageBox();
    const renderKey = `${currentPage}|${box.width}x${box.height}|z${zoomScale.toFixed(2)}`;
    if (renderKey === lastRenderKeyRef.current) return;

    const gen = ++renderGenRef.current;
    setRendering(true);

    try {
      const page = await pdf.getPage(currentPage + 1);
      if (gen !== renderGenRef.current) return;

      const ok = await renderPageToCanvas(
        page,
        canvas,
        box,
        zoomScale,
        renderTasksRef.current,
        currentPage,
        'contain',
        { constrainToBox: zoomScale <= panThreshold },
      );
      if (gen !== renderGenRef.current) return;
      if (ok) {
        lastRenderKeyRef.current = renderKey;
        if (zoomScale > panThreshold) {
          requestAnimationFrame(() => {
            const vp = viewportRef.current;
            if (!vp || gen !== renderGenRef.current) return;
            const maxX = Math.max(0, vp.scrollWidth - vp.clientWidth);
            const maxY = Math.max(0, vp.scrollHeight - vp.clientHeight);
            const pending = pendingZoomScrollRef.current;
            pendingZoomScrollRef.current = null;
            if (pending?.mode === 'center') {
              vp.scrollLeft = Math.floor(maxX / 2);
              vp.scrollTop = Math.floor(maxY / 2);
            } else if (pending?.mode === 'ratio') {
              vp.scrollLeft = Math.min(maxX, Math.max(0, pending.cx * pending.ratio - vp.clientWidth / 2));
              vp.scrollTop = Math.min(maxY, Math.max(0, pending.cy * pending.ratio - vp.clientHeight / 2));
            } else {
              vp.scrollLeft = Math.min(Math.max(0, vp.scrollLeft), maxX);
              vp.scrollTop = Math.min(Math.max(0, vp.scrollTop), maxY);
            }
          });
        }
      }
    } catch (err) {
      console.error('Page render failed:', err);
    } finally {
      if (gen === renderGenRef.current) setRendering(false);
    }
  }, [currentPage, getPageBox, phase, zoomScale, embedded, panThreshold]);

  const scheduleRender = useCallback(() => {
    if (phase !== 'ready' || pageCount === 0) return;
    invalidateRender();
    renderCurrentPage();
  }, [phase, pageCount, invalidateRender, renderCurrentPage]);

  const setZoomClamped = useCallback((next) => {
    // Anything at/under pan threshold snaps to comfort (132% on phone) for swipe-ready reading.
    let z = Math.min(maxZoom, Math.max(comfortZoom, next));
    if (z <= panThreshold) z = comfortZoom;
    const vp = viewportRef.current;
    const prev = zoomScale;
    gestureZoomRef.current = null;
    setGestureZoom(null);
    setZoomScale(z);
    if (z <= panThreshold) {
      pendingZoomScrollRef.current = null;
      if (vp) {
        vp.scrollLeft = 0;
        vp.scrollTop = 0;
      }
      return;
    }
    if (vp && prev <= panThreshold) {
      pendingZoomScrollRef.current = { mode: 'center' };
    } else if (vp && prev > 0 && z !== prev) {
      pendingZoomScrollRef.current = {
        mode: 'ratio',
        cx: vp.scrollLeft + vp.clientWidth / 2,
        cy: vp.scrollTop + vp.clientHeight / 2,
        ratio: z / prev,
      };
    }
  }, [zoomScale, maxZoom, comfortZoom, panThreshold]);

  const snapZoom = useCallback((value) => {
    const steps = ZOOM_STEPS.filter((s) => s >= comfortZoom - 0.001 && s <= maxZoom + 0.001);
    const list = steps.length ? steps : ZOOM_STEPS;
    return list.reduce((best, s) =>
      Math.abs(s - value) < Math.abs(best - value) ? s : best
    );
  }, [comfortZoom, maxZoom]);

  const zoomIn = () => {
    const next = ZOOM_STEPS.find((s) => s > zoomScale + 0.02 && s <= maxZoom) ?? maxZoom;
    setZoomClamped(Math.min(maxZoom, next));
  };

  const zoomOut = () => {
    const prev = [...ZOOM_STEPS].reverse().find((s) => s < zoomScale - 0.02 && s >= comfortZoom) ?? comfortZoom;
    setZoomClamped(prev);
  };

  const zoomReset = () => setZoomClamped(comfortZoom);

  const zoomActionsRef = useRef({ zoomIn, zoomOut });
  zoomActionsRef.current = { zoomIn, zoomOut };

  const dismissSwipeHint = useCallback(() => {
    setShowSwipeHint(false);
    try {
      sessionStorage.setItem(SWIPE_HINT_KEY, '1');
    } catch {
      /* ignore */
    }
  }, []);

  const goToPage = useCallback(
    (idx, direction = 0) => {
      const maxIdx = pageLimit != null ? Math.min(pageCount, pageLimit) - 1 : pageCount - 1;
      if (pageLimit != null && idx > maxIdx) {
        onPreviewSubscribe?.();
        return;
      }
      const next = Math.max(0, Math.min(maxIdx, idx));
      if (next === currentPage) return;
      cancelRenderTask(renderTasksRef.current[currentPage]);
      delete renderTasksRef.current[currentPage];
      lastRenderKeyRef.current = '';
      if (direction) {
        setPageTransition(direction > 0 ? 'pv-slide-left' : 'pv-slide-right');
        setTimeout(() => setPageTransition(''), 280);
      }
      setCurrentPage(next);
      setSwipeOffset(0);
      setZoomClamped(comfortZoom);
      if (viewportRef.current) {
        viewportRef.current.scrollLeft = 0;
        viewportRef.current.scrollTop = 0;
      }
      if (direction) void hapticLight();
    },
    [pageCount, currentPage, setZoomClamped, pageLimit, onPreviewSubscribe, comfortZoom]
  );

  const atPreviewEnd = pageLimit != null && currentPage >= pageLimit - 1;

  const goPrev = () => goToPage(currentPage - 1, -1);
  const goNext = () => goToPage(currentPage + 1, 1);

  const displayZoom = gestureZoom ?? zoomScale;
  const canSwipePages = displayZoom <= panThreshold;
  const isPanningZoom = displayZoom > panThreshold;

  useLayoutEffect(() => {
    const update = () => setIsMobile(detectMobileView());
    update();
    window.addEventListener('resize', update);
    window.addEventListener('orientationchange', update);
    const vv = window.visualViewport;
    vv?.addEventListener('resize', update);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('orientationchange', update);
      vv?.removeEventListener('resize', update);
    };
  }, []);

  useLayoutEffect(() => {
    const setVh = () => {
      const h = window.visualViewport?.height ?? window.innerHeight;
      backdropRef.current?.style.setProperty('--pv-vh', `${Math.round(h)}px`);
    };
    setVh();
    window.visualViewport?.addEventListener('resize', setVh);
    window.addEventListener('resize', setVh);
    return () => {
      window.visualViewport?.removeEventListener('resize', setVh);
      window.removeEventListener('resize', setVh);
    };
  }, []);

  useEffect(() => {
    try {
      setShowSwipeHint(!sessionStorage.getItem(SWIPE_HINT_KEY));
    } catch {
      setShowSwipeHint(true);
    }
  }, []);

  useEffect(() => {
    if (!showSwipeHint) return;
    const timer = setTimeout(dismissSwipeHint, 5000);
    return () => clearTimeout(timer);
  }, [showSwipeHint, dismissSwipeHint]);

  useEffect(() => {
    document.body.classList.add('pv-open');
    const meta = document.querySelector('meta[name="viewport"]');
    const prevContent = meta?.getAttribute('content') ?? '';
    if (!isMobile) {
      meta?.setAttribute(
        'content',
        'width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no, viewport-fit=cover'
      );
    }

    const onKey = (e) => {
      const key = e.key?.toLowerCase();
      if ((e.ctrlKey || e.metaKey) && ['s', 'p', 'u', 'a', 'c'].includes(key)) e.preventDefault();
      if (key === 'printscreen') e.preventDefault();
      if (key === '+' || key === '=') zoomIn();
      if (key === '-') zoomOut();
      if (key === '0') zoomReset();
      if (key === 'arrowleft' && canSwipePages) goPrev();
      if (key === 'arrowright' && canSwipePages) goNext();
      if (key === 'escape' && !embedded) onClose();
    };
    const blockCopy = (e) => e.preventDefault();
    const blockWheelZoom = (e) => {
      if (!(e.ctrlKey || e.metaKey)) return;
      const inViewport = e.target.closest?.('.pv-page-viewport');
      if (inViewport) {
        e.preventDefault();
        if (e.deltaY < 0) zoomActionsRef.current.zoomIn();
        else if (e.deltaY > 0) zoomActionsRef.current.zoomOut();
        return;
      }
      e.preventDefault();
    };

    document.addEventListener('keydown', onKey);
    document.addEventListener('copy', blockCopy);
    document.addEventListener('cut', blockCopy);
    document.addEventListener('contextmenu', blockCopy);
    document.addEventListener('wheel', blockWheelZoom, { passive: false });

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    if (!embedded) document.body.style.touchAction = 'none';

    return () => {
      document.body.classList.remove('pv-open');
      if (meta && prevContent) meta.setAttribute('content', prevContent);
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('copy', blockCopy);
      document.removeEventListener('cut', blockCopy);
      document.removeEventListener('contextmenu', blockCopy);
      document.removeEventListener('wheel', blockWheelZoom);
      document.body.style.overflow = prevOverflow;
      document.documentElement.style.overflow = '';
      document.body.style.touchAction = '';
    };
  }, [canSwipePages, currentPage, pageCount, onClose, embedded]);

  useEffect(() => {
    if (!pdfPath || waitingForPath) return;

    const loadGen = ++loadGenRef.current;
    let loadingTask = null;
    let loaded = false;

    setPhase('loading');
    setPageCount(0);
    setCurrentPage(loadPaperPage(resumeKey || dateLabel));
    setZoomScale(comfortZoom);
    lastRenderKeyRef.current = '';
    cancelAllRenderTasks();

    if (pdfRef.current) {
      pdfRef.current.destroy();
      pdfRef.current = null;
    }

    const openDocument = async () => {
      let cached = getCachedPdfBuffer(pdfPath);
      if (!cached) {
        try {
          cached = await getPdfBytes(pdfPath);
        } catch {
          cached = null;
        }
      }
      if (cached) {
        return pdfjsLib.getDocument({
          data: cached,
          disableFontFace: true,
          useSystemFonts: true,
        });
      }

      // Full-file fetch is more reliable than ranged requests behind auth tokens / nginx.
      prefetchPdfBytes(pdfPath);
      try {
        const bytes = await getPdfBytes(pdfPath);
        if (bytes) {
          return pdfjsLib.getDocument({
            data: bytes,
            disableFontFace: true,
            useSystemFonts: true,
          });
        }
      } catch {
        /* fall through to URL */
      }

      return pdfjsLib.getDocument({
        url: pdfPath,
        withCredentials: true,
        disableFontFace: true,
        useSystemFonts: true,
        disableRange: true,
        disableStream: true,
        disableAutoFetch: false,
      });
    };

    (async () => {
      try {
        loadingTask = await openDocument();
        if (!loadingTask || loadGen !== loadGenRef.current) return;

        const pdf = await loadingTask.promise;
        loaded = true;

        if (loadGen !== loadGenRef.current) {
          pdf.destroy();
          return;
        }

        pdfRef.current = pdf;
        setPageCount(pdf.numPages);
        setPhase('ready');
      } catch (err) {
        if (loadGen !== loadGenRef.current) return;
        console.error('PDF load failed:', err);
        // Drop possibly detached/stale cache so Try again fetches a fresh copy.
        clearPaperCache();
        setPhase('error');
      }
    })();

    return () => {
      loadGenRef.current += 1;
      cancelAllRenderTasks();
      if (pdfRef.current) {
        pdfRef.current.destroy();
        pdfRef.current = null;
      }
      if (loadingTask && !loaded) {
        try {
          loadingTask.destroy();
        } catch {
          /* ignore */
        }
      }
    };
  }, [pdfPath, waitingForPath, cancelAllRenderTasks, reloadNonce, resumeKey, dateLabel, comfortZoom]);

  useEffect(() => {
    if ((resumeKey || dateLabel) && phase === 'ready') savePaperPage(resumeKey || dateLabel, currentPage);
  }, [currentPage, resumeKey, dateLabel, phase]);

  useLayoutEffect(() => {
    if (phase !== 'ready' || pageCount === 0) return;
    let tmr;
    const run = () => {
      clearTimeout(tmr);
      tmr = setTimeout(scheduleRender, 0);
    };
    run();
    const ro = new ResizeObserver(run);
    if (viewportRef.current) ro.observe(viewportRef.current);
    const vv = window.visualViewport;
    vv?.addEventListener('resize', run);
    window.addEventListener('orientationchange', run);
    return () => {
      clearTimeout(tmr);
      ro.disconnect();
      vv?.removeEventListener('resize', run);
      window.removeEventListener('orientationchange', run);
    };
  }, [phase, pageCount, scheduleRender, currentPage, zoomScale, isMobile]);

  useEffect(() => {
    if (phase !== 'ready' || gestureZoom != null) return;
    clearTimeout(zoomDebounceRef.current);
    zoomDebounceRef.current = setTimeout(scheduleRender, 0);
    return () => clearTimeout(zoomDebounceRef.current);
  }, [zoomScale, currentPage, phase, scheduleRender, gestureZoom]);

  const handleTouchStart = (e) => {
    if (e.touches.length === 2) {
      const base = gestureZoomRef.current ?? zoomScale;
      pinchRef.current = { active: true, dist0: getTouchDistance(e.touches), scale0: base };
      touchRef.current.active = false;
      panRef.current.active = false;
      return;
    }
    if (e.touches.length !== 1) return;

    // Zoomed past comfort: one-finger drag pans (never changes page).
    if ((gestureZoomRef.current ?? zoomScale) > panThreshold) {
      const vp = viewportRef.current;
      panRef.current = {
        active: true,
        x: e.touches[0].clientX,
        y: e.touches[0].clientY,
        scrollLeft: vp?.scrollLeft || 0,
        scrollTop: vp?.scrollTop || 0,
      };
      touchRef.current.active = false;
      return;
    }

    // Fit / comfort view: horizontal swipe changes page.
    touchRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY, active: true };
    panRef.current.active = false;
  };

  const handleTouchMove = (e) => {
    if (e.touches.length === 2 && pinchRef.current.active) {
      e.preventDefault();
      const dist = getTouchDistance(e.touches);
      if (pinchRef.current.dist0 > 0) {
        const next = Math.min(
          maxZoom,
          Math.max(comfortZoom, pinchRef.current.scale0 * (dist / pinchRef.current.dist0))
        );
        gestureZoomRef.current = next;
        setGestureZoom(next);
      }
      return;
    }

    if (panRef.current.active && e.touches.length === 1) {
      e.preventDefault();
      const vp = viewportRef.current;
      if (!vp) return;
      const dx = e.touches[0].clientX - panRef.current.x;
      const dy = e.touches[0].clientY - panRef.current.y;
      vp.scrollLeft = panRef.current.scrollLeft - dx;
      vp.scrollTop = panRef.current.scrollTop - dy;
      return;
    }

    if (!touchRef.current.active || e.touches.length !== 1) return;
    const dx = e.touches[0].clientX - touchRef.current.x;
    const dy = e.touches[0].clientY - touchRef.current.y;
    // Horizontal swipe intent — page turn feedback.
    if (Math.abs(dx) > 12 && Math.abs(dx) > Math.abs(dy) * 1.15) {
      e.preventDefault();
      setSwipeOffset(Math.max(-window.innerWidth * 0.4, Math.min(window.innerWidth * 0.4, dx)));
    }
  };

  const handleTouchEnd = (e) => {
    if (pinchRef.current.active) {
      pinchRef.current.active = false;
      const live = gestureZoomRef.current ?? zoomScale;
      setZoomClamped(snapZoom(live));
      return;
    }
    if (panRef.current.active) {
      const dx = e.changedTouches[0].clientX - panRef.current.x;
      const dy = e.changedTouches[0].clientY - panRef.current.y;
      panRef.current.active = false;
      if (Math.hypot(dx, dy) < 14) {
        const now = Date.now();
        if (now - lastTapRef.current < 340) {
          lastTapRef.current = 0;
          setZoomClamped(comfortZoom);
          return;
        }
        lastTapRef.current = now;
      }
      return;
    }
    if (!touchRef.current.active) return;
    touchRef.current.active = false;
    const dx = e.changedTouches[0].clientX - touchRef.current.x;
    const dy = e.changedTouches[0].clientY - touchRef.current.y;
    setSwipeOffset(0);

    // Double-tap toggles reading zoom (comfort ↔ ~2.4×).
    if (Math.hypot(dx, dy) < 14) {
      const now = Date.now();
      if (now - lastTapRef.current < 340) {
        lastTapRef.current = 0;
        const readZoom = Math.min(maxZoom, Math.max(2.35, comfortZoom * 1.8));
        setZoomClamped(zoomScale > panThreshold + 0.12 ? comfortZoom : readZoom);
        return;
      }
      lastTapRef.current = now;
      return;
    }

    if (pageCount <= 1) return;
    if (Math.abs(dx) < SWIPE_THRESHOLD || Math.abs(dx) < Math.abs(dy) * 1.25) return;
    dismissSwipeHint();
    if (dx < 0 && currentPage < maxPageIndex) goNext();
    else if (dx > 0 && currentPage > 0) goPrev();
  };

  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return;
    const onTouchMove = (e) => {
      // Block browser navigation gestures; we own pinch + pan/swipe.
      if (e.touches.length === 2 || panRef.current.active || touchRef.current.active) {
        e.preventDefault();
      }
    };
    el.addEventListener('touchmove', onTouchMove, { passive: false });
    return () => el.removeEventListener('touchmove', onTouchMove);
  }, [phase]);

  // Desktop: click-drag pans the page when zoomed so left/top edges stay reachable.
  useEffect(() => {
    if (isMobile || phase !== 'ready') return;
    const el = viewportRef.current;
    if (!el) return;

    const onDown = (e) => {
      if ((gestureZoomRef.current ?? zoomScale) <= panThreshold) return;
      if (e.button !== 0) return;
      panRef.current = {
        active: true,
        x: e.clientX,
        y: e.clientY,
        scrollLeft: el.scrollLeft,
        scrollTop: el.scrollTop,
      };
      el.classList.add('pv-dragging');
      e.preventDefault();
    };
    const onMove = (e) => {
      if (!panRef.current.active) return;
      el.scrollLeft = panRef.current.scrollLeft - (e.clientX - panRef.current.x);
      el.scrollTop = panRef.current.scrollTop - (e.clientY - panRef.current.y);
    };
    const onUp = () => {
      panRef.current.active = false;
      el.classList.remove('pv-dragging');
    };

    el.addEventListener('mousedown', onDown);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      el.removeEventListener('mousedown', onDown);
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [isMobile, phase, zoomScale, currentPage, panThreshold]);

  const handleShare = () => {
    const site = SITE_URL || window.location.origin || 'https://repatikosam.com';
    const shareUrl = `${site.replace(/\/$/, '')}/`;
    const text = `${t.shareMessage}${dateLabel ? ` — ${dateLabel}` : ''}`;
    void shareLink({ title: t.brandName, text, url: shareUrl });
  };

  const zoomPercent = Math.round(displayZoom * 100);
  const pinchPreviewScale = gestureZoom != null && zoomScale > 0 ? gestureZoom / zoomScale : 1;
  const maxPageIndex =
    pageLimit != null ? Math.min(pageCount, pageLimit) - 1 : pageCount - 1;

  const toolbarProps = {
    t,
    dateLabel,
    phase,
    pageCount,
    currentPage,
    zoomScale: displayZoom,
    zoomPercent,
    minZoom: comfortZoom,
    maxZoom,
    onZoomIn: zoomIn,
    onZoomOut: zoomOut,
    onZoomReset: zoomReset,
    onShare: handleShare,
    onClose,
    hideClose: embedded,
  };

  const pageStage = phase === 'ready' && (
    <div
      className={`pv-page-stage ${pageTransition}${isPanningZoom ? ' pv-page-stage--zoomed' : ''}`}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {rendering && <div className="pv-render-overlay" aria-hidden />}
      {showSwipeHint && pageCount > 1 && canSwipePages && isMobile && (
        <div className="pv-swipe-hint" onClick={dismissSwipeHint} role="status">
          <span className="pv-swipe-arrow">←</span>
          <span className="pv-swipe-text">{t.paperSwipeHint}</span>
          <span className="pv-swipe-arrow">→</span>
        </div>
      )}
      {/* Swipe turns pages — hide floating side arrows on phone. */}
      <div className={`pv-page-viewport${isPanningZoom ? ' pv-page-viewport--zoomed' : ''}`} ref={viewportRef}>
        <div
          className={`pv-page-inner ${gestureZoom != null ? 'pv-page-inner--gesture' : ''}`}
          style={{
            ...(canSwipePages && isMobile ? { transform: `translateX(${swipeOffset}px)` } : {}),
            ...(gestureZoom != null
              ? {
                  transform: `${canSwipePages && isMobile ? `translateX(${swipeOffset}px) ` : ''}scale(${pinchPreviewScale})`,
                }
              : {}),
          }}
        >
          <canvas ref={canvasRef} className="pv-page-canvas" draggable={false} />
          <div className="pv-watermark" aria-hidden="true">{t.shareMessage}</div>
        </div>
      </div>
      {isMobile && isPanningZoom && !embedded && <p className="pv-pinch-hint">{t.paperPinchHint}</p>}
      {embedded && phase === 'ready' && pageCount > 0 ? (
        <div className="pv-embed-chrome">
          {isPanningZoom ? (
            <div className="pv-zoom-fab" role="group" aria-label={t.paperZoomLabel}>
              <button type="button" className="pv-zoom-btn" onClick={zoomOut} disabled={displayZoom <= comfortZoom + 0.01} aria-label={t.paperZoomOut}>−</button>
              <span className="pv-zoom-level">{zoomPercent}%</span>
              <button type="button" className="pv-zoom-btn" onClick={zoomIn} disabled={displayZoom >= maxZoom - 0.01} aria-label={t.paperZoomIn}>+</button>
              <button type="button" className="pv-zoom-reset" onClick={zoomReset}>{t.paperZoomReset}</button>
            </div>
          ) : null}
          <div className="pv-progress" role="progressbar" aria-valuenow={currentPage + 1} aria-valuemin={1} aria-valuemax={pageCount}>
            <span style={{ width: `${((currentPage + 1) / pageCount) * 100}%` }} />
            <em>
              {currentPage + 1} / {pageCount}
            </em>
          </div>
        </div>
      ) : null}
    </div>
  );

  return (
    <div
      ref={backdropRef}
      className={`pv-backdrop pv-secure ${isMobile ? 'pv-mobile' : 'pv-desktop'}${embedded ? ' pv-embedded' : ''}`}
      onClick={embedded ? undefined : onClose}
      onContextMenu={(e) => e.preventDefault()}
      role="dialog"
      aria-modal={!embedded}
    >
      <div className="pv-modal" onClick={(e) => e.stopPropagation()}>
        {isMobile ? (
          <div className="pv-scroll" ref={scrollRef}>
            {!embedded && <Toolbar {...toolbarProps} />}
            <div className="pv-body">
              {(phase === 'loading' || waitingForPath || !pdfPath) && (
                <div className="pv-status"><div className="pv-spinner" /><p>{t.loading}</p></div>
              )}
              {phase === 'error' && (
                <div className="pv-status pv-status-error">
                  <p>{t.serverError}</p>
                  <button type="button" className="pv-retry-btn" onClick={() => setReloadNonce((n) => n + 1)}>{t.paperRetry}</button>
                  <button type="button" className="pv-retry-btn pv-retry-secondary" onClick={onClose}>{t.closeBtn}</button>
                </div>
              )}
              {pageStage}
            </div>
            {phase === 'ready' && pageCount > 0 && !embedded && (
              <footer className="pv-reader-footer" ref={footerRef}>
                <div className="pv-pager">
                  <button type="button" className="pv-pager-btn" onClick={goPrev} disabled={currentPage === 0}>← {t.paperPrev}</button>
                  <span className="pv-pager-dots">
                    {Array.from({ length: Math.min(pageCount, 11) }, (_, i) => (
                      <button key={i} type="button" className={`pv-dot-btn ${i === currentPage ? 'active' : ''}`} onClick={() => goToPage(i)} aria-label={`${t.paperPageOf} ${i + 1}`} />
                    ))}
                  </span>
                  <button type="button" className="pv-pager-btn" onClick={goNext} disabled={currentPage >= maxPageIndex}>{t.paperNext} →</button>
                </div>
              </footer>
            )}
          </div>
        ) : (
          <div className="pv-desktop-shell">
            <Toolbar {...toolbarProps} />
            <div className={`pv-desktop-body ${phase === 'loading' || phase === 'error' || waitingForPath || !pdfPath ? 'pv-desktop-body--status' : ''}`}>
              {(phase === 'loading' || waitingForPath || !pdfPath) && (
                <div className="pv-status-overlay" role="status">
                  <div className="pv-status">
                    <div className="pv-spinner" />
                    <p>{t.loading}</p>
                  </div>
                </div>
              )}
              {phase === 'error' && (
                <div className="pv-status-overlay" role="status">
                  <div className="pv-status pv-status-error">
                    <p>{t.serverError}</p>
                    <button type="button" className="pv-retry-btn" onClick={() => setReloadNonce((n) => n + 1)}>{t.paperRetry}</button>
                    <button type="button" className="pv-retry-btn pv-retry-secondary" onClick={onClose}>{t.closeBtn}</button>
                  </div>
                </div>
              )}
              {phase === 'ready' && pageCount > 0 && (
                <aside className="pv-pages-rail" aria-label={t.paperPagesAria || t.paperPageOf}>
                  {Array.from({ length: pageCount }, (_, i) => (
                    <button
                      key={i}
                      type="button"
                      className={`pv-rail-btn ${i === currentPage ? 'active' : ''}`}
                      onClick={() => goToPage(i)}
                    >
                      <span className="pv-rail-num">{i + 1}</span>
                      <span className="pv-rail-label">Page</span>
                    </button>
                  ))}
                </aside>
              )}
              <div className="pv-desktop-stage-wrap">
                {pageStage}
              </div>
            </div>
            {phase === 'ready' && pageCount > 1 && (
              <footer className="pv-desktop-footer">
                <button type="button" className="pv-desk-foot-btn" onClick={goPrev} disabled={currentPage === 0}>← {t.paperPrev}</button>
                <input
                  type="range"
                  className="pv-page-slider"
                  min={0}
                  max={Math.max(0, maxPageIndex)}
                  value={currentPage}
                  onChange={(e) => goToPage(Number(e.target.value))}
                  aria-label={t.paperSliderAria || t.paperPageOf}
                />
                <span className="pv-slider-label">{currentPage + 1} / {pageCount}</span>
                <button type="button" className="pv-desk-foot-btn" onClick={goNext} disabled={currentPage >= maxPageIndex}>{t.paperNext} →</button>
                {!canSwipePages && <span className="pv-desk-zoom-hint">{t.paperPinchHint}</span>}
              </footer>
            )}
          </div>
        )}
        {contentShield && <div className="pv-shield" aria-hidden="true" />}
      </div>
      {!embedded && pageLimit != null && (
        <div className="pv-preview-banner" data-sub-free>
          <span>{t.previewLimitBanner}</span>
          <button type="button" onClick={() => onPreviewSubscribe?.()}>
            {t.previewSubscribeCta}
          </button>
        </div>
      )}
    </div>
  );
}
